/**
 * Admin Blog tab — draft, generate (via Claude), review, publish/unpublish, delete.
 *
 * Workflow:
 *   1. Choose source (custom topic OR jurisprudence ruling from a picker)
 *   2. Click "Generate draft" → Claude Sonnet 4.6 drafts title, meta, tags, body
 *   3. Preview + inline edit fields, then Save as draft OR Save & Publish
 *   4. Table below lists all posts (draft + published) with Publish/Unpublish/Delete
 */
import React, { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useI18n } from "@/contexts/I18nContext";
import { api, extractErrorCode } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import { toast } from "sonner";
import { Sparkles, Loader2, Save, Eye, EyeOff, Trash2, Wand2, ExternalLink, FileText, CalendarClock, PlayCircle, BookOpen } from "lucide-react";

const EMPTY_DRAFT = {
  post_id: null,
  title: "",
  meta_description: "",
  keyword: "",
  tags: "",
  locale: "it",
  body_markdown: "",
  hero_image_url: "",
  source_ruling_id: "",
};

function formatDateTime(iso, locale = "it") {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(locale === "it" ? "it-IT" : locale === "pl" ? "pl-PL" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso.slice(0, 16).replace("T", " ");
  }
}

export default function BlogTab() {
  const { t } = useI18n();
  const [posts, setPosts] = useState([]);
  const [counts, setCounts] = useState({ draft: 0, published: 0 });
  const [loading, setLoading] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [runningNow, setRunningNow] = useState(false);

  // Generator state
  const [topic, setTopic] = useState("");
  const [keyword, setKeyword] = useState("");
  const [locale, setLocale] = useState("it");
  const [rulingHeadline, setRulingHeadline] = useState("");
  const [rulingCourt, setRulingCourt] = useState("");
  const [rulingNumber, setRulingNumber] = useState("");
  const [rulingDate, setRulingDate] = useState("");
  const [rulingPrinciple, setRulingPrinciple] = useState("");
  const [generating, setGenerating] = useState(false);

  // Draft editor
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const [postsRes, statusRes] = await Promise.all([
        api.get("/admin/blog/posts"),
        api.get("/admin/blog/scheduler/status").catch(() => null),
      ]);
      setPosts(postsRes.data.posts || []);
      setCounts(postsRes.data.counts || { draft: 0, published: 0 });
      if (statusRes) setSchedulerStatus(statusRes.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useImperativeLoad(() => { fetchPosts(); }, []);

  // Deep-link support: /admin?tab=blog&post_id=xxx from cron email opens that draft
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const postId = params.get("post_id");
    if (postId && posts.length > 0) {
      const target = posts.find((p) => p.post_id === postId);
      if (target) {
        setDraft({
          post_id: target.post_id,
          title: target.title || "",
          meta_description: target.meta_description || "",
          keyword: target.keyword || "",
          tags: (target.tags || []).join(", "),
          locale: target.locale || "it",
          body_markdown: target.body_markdown || "",
          hero_image_url: target.hero_image_url || "",
          source_ruling_id: target.source_ruling_id || "",
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
        window.history.replaceState({}, "", "/admin?tab=blog");
      }
    }
  }, [posts]);

  const handleRunNow = async () => {
    if (!window.confirm(t("admin.blog.confirm_run_now"))) return;
    setRunningNow(true);
    try {
      const r = await api.post("/admin/blog/scheduler/run-now", {});
      const s = r.data?.status;
      if (s === "success") {
        toast.success(t("admin.blog.run_now_success").replace("{ruling}", r.data.ruling_id || ""));
        fetchPosts();
      } else {
        toast.error(t("admin.blog.run_now_error") + ": " + (r.data?.message || ""));
      }
    } catch (e) {
      toast.error(extractErrorCode(e) || t("admin.blog.run_now_error"));
    } finally {
      setRunningNow(false);
    }
  };

  const handleGenerate = async () => {
    if (topic.trim().length < 5) {
      toast.error(t("admin.blog.err_topic_short"));
      return;
    }
    setGenerating(true);
    try {
      const r = await api.post("/admin/blog/generate", {
        topic: topic.trim(),
        keyword: keyword.trim() || null,
        locale,
        ruling_headline: rulingHeadline.trim() || null,
        ruling_court: rulingCourt.trim() || null,
        ruling_number: rulingNumber.trim() || null,
        ruling_date: rulingDate.trim() || null,
        ruling_principle: rulingPrinciple.trim() || null,
      });
      setDraft({
        post_id: null,
        title: r.data.title || "",
        meta_description: r.data.meta_description || "",
        keyword: r.data.keyword || keyword,
        tags: (r.data.tags || []).join(", "),
        locale: r.data.locale || locale,
        body_markdown: r.data.body_markdown || "",
        hero_image_url: "",
        source_ruling_id: r.data.source_ruling_id || "",
      });
      toast.success(t("admin.blog.gen_success"));
    } catch (e) {
      toast.error(extractErrorCode(e) || t("admin.blog.gen_error"));
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async (publish = false) => {
    if (draft.title.length < 3 || draft.meta_description.length < 10 || draft.body_markdown.length < 100) {
      toast.error(t("admin.blog.err_missing_fields"));
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/admin/blog/posts", {
        post_id: draft.post_id,
        title: draft.title.trim(),
        meta_description: draft.meta_description.trim(),
        keyword: draft.keyword.trim() || null,
        tags: draft.tags.split(",").map((s) => s.trim()).filter(Boolean),
        locale: draft.locale,
        body_markdown: draft.body_markdown,
        hero_image_url: draft.hero_image_url.trim() || null,
        source_ruling_id: draft.source_ruling_id || null,
        publish,
      });
      toast.success(publish ? t("admin.blog.save_publish_success") : t("admin.blog.save_draft_success"));
      setDraft({ ...EMPTY_DRAFT, locale });
      fetchPosts();
      if (publish && r.data.slug) {
        // Show a quick link to the live post
        setTimeout(() => {
          toast(t("admin.blog.view_live"), {
            action: {
              label: t("admin.blog.open"),
              onClick: () => window.open(`/blog/${r.data.slug}`, "_blank"),
            },
          });
        }, 400);
      }
    } catch (e) {
      toast.error(extractErrorCode(e) || t("admin.blog.save_error"));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (post) => {
    setDraft({
      post_id: post.post_id,
      title: post.title || "",
      meta_description: post.meta_description || "",
      keyword: post.keyword || "",
      tags: (post.tags || []).join(", "),
      locale: post.locale || "it",
      body_markdown: post.body_markdown || "",
      hero_image_url: post.hero_image_url || "",
      source_ruling_id: post.source_ruling_id || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleTogglePublish = async (post) => {
    try {
      const url = post.status === "published"
        ? `/admin/blog/posts/${post.post_id}/unpublish`
        : `/admin/blog/posts/${post.post_id}/publish`;
      await api.post(url);
      toast.success(post.status === "published" ? t("admin.blog.unpublished") : t("admin.blog.published"));
      fetchPosts();
    } catch (e) {
      toast.error(extractErrorCode(e) || t("admin.blog.action_error"));
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(t("admin.blog.confirm_delete").replace("{title}", post.title))) return;
    try {
      await api.delete(`/admin/blog/posts/${post.post_id}`);
      toast.success(t("admin.blog.deleted"));
      fetchPosts();
    } catch (e) {
      toast.error(extractErrorCode(e) || t("admin.blog.action_error"));
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-blog-tab">
      {/* Scheduler status card */}
      {schedulerStatus && (
        <Card className="border-slate-800 bg-gradient-to-br from-[#0B0E5C]/40 to-slate-900/60 p-5" data-testid="admin-blog-scheduler">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#9ACD32]/15 text-[#9ACD32]">
                <CalendarClock className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-display text-base font-bold text-white">{t("admin.blog.scheduler_title")}</h3>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${
                    schedulerStatus.enabled ? "bg-[#9ACD32]/15 text-[#9ACD32]" : "bg-slate-800 text-slate-400"
                  }`}>
                    {schedulerStatus.enabled ? "ON" : "OFF"}
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-400">{t("admin.blog.scheduler_desc")}</p>
                <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-[11px] text-slate-300 sm:grid-cols-3">
                  <div>
                    <span className="text-slate-500">{t("admin.blog.next_run")}: </span>
                    <span className="font-mono">{formatDateTime(schedulerStatus.next_run_at)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">{t("admin.blog.corpus_used")}: </span>
                    <span className="font-mono">{schedulerStatus.corpus_used} / {schedulerStatus.corpus_size}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">{t("admin.blog.recipients")}: </span>
                    <span className="font-mono">{schedulerStatus.admin_recipients?.length || 0}</span>
                  </div>
                </div>
                {schedulerStatus.last_run?.finished_at && (
                  <div className="mt-3 rounded-md border border-slate-800 bg-slate-950/40 p-2.5 text-[11px]">
                    <div className="flex items-center gap-2">
                      <span className={`inline-block h-2 w-2 rounded-full ${
                        schedulerStatus.last_run.status === "success" ? "bg-[#9ACD32]" :
                        schedulerStatus.last_run.status === "error" ? "bg-red-500" :
                        "bg-slate-500"
                      }`} />
                      <span className="font-mono uppercase tracking-wider text-slate-400">
                        {t("admin.blog.last_run")}: {schedulerStatus.last_run.status || "—"}
                      </span>
                      <span className="text-slate-500">·</span>
                      <span className="text-slate-400">{formatDateTime(schedulerStatus.last_run.finished_at)}</span>
                    </div>
                    {schedulerStatus.last_run.message && (
                      <p className="mt-1 text-slate-400">{schedulerStatus.last_run.message}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
            <Button
              onClick={handleRunNow}
              disabled={runningNow}
              variant="outline"
              className="border-[#9ACD32]/40 bg-[#9ACD32]/10 text-[#9ACD32] hover:bg-[#9ACD32]/20"
              data-testid="admin-blog-run-now"
            >
              {runningNow ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlayCircle className="mr-2 h-4 w-4" />}
              {runningNow ? t("admin.blog.running_now") : t("admin.blog.run_now_btn")}
            </Button>
          </div>
        </Card>
      )}

      {/* Counters */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-slate-800 bg-slate-900/60 p-4">
          <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{t("admin.blog.count_drafts")}</p>
          <p className="mt-1 font-display text-2xl font-extrabold text-white" data-testid="admin-blog-count-drafts">{counts.draft}</p>
        </Card>
        <Card className="border-slate-800 bg-slate-900/60 p-4">
          <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{t("admin.blog.count_published")}</p>
          <p className="mt-1 font-display text-2xl font-extrabold text-[#9ACD32]" data-testid="admin-blog-count-published">{counts.published}</p>
        </Card>
        <Card className="border-slate-800 bg-slate-900/60 p-4">
          <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{t("admin.blog.count_total")}</p>
          <p className="mt-1 font-display text-2xl font-extrabold text-white">{counts.draft + counts.published}</p>
        </Card>
      </div>

      {/* Generator + Editor */}
      <Card className="border-slate-800 bg-slate-900/60 p-6" data-testid="admin-blog-editor">
        <div className="mb-6 flex items-center gap-2">
          <Wand2 className="h-4 w-4 text-[#9ACD32]" />
          <h3 className="font-display text-lg font-bold text-white">{t("admin.blog.gen_title")}</h3>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div>
            <Label className="text-slate-300">{t("admin.blog.topic_label")}</Label>
            <Textarea
              rows={2}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={t("admin.blog.topic_ph")}
              className="mt-1 border-slate-700 bg-slate-950 text-white"
              data-testid="admin-blog-topic"
            />
          </div>
          <div>
            <Label className="text-slate-300">{t("admin.blog.keyword_label")}</Label>
            <Input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder={t("admin.blog.keyword_ph")}
              className="mt-1 border-slate-700 bg-slate-950 text-white"
              data-testid="admin-blog-keyword"
            />
          </div>
          <div>
            <Label className="text-slate-300">{t("admin.blog.locale_label")}</Label>
            <Select value={locale} onValueChange={setLocale}>
              <SelectTrigger className="mt-1 border-slate-700 bg-slate-950 text-white" data-testid="admin-blog-locale">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-900 text-white">
                <SelectItem value="it">Italiano</SelectItem>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="pl">Polski</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <details className="mt-4 rounded-lg border border-slate-800 bg-slate-950/50 p-4">
          <summary className="cursor-pointer text-sm font-semibold text-slate-300">{t("admin.blog.ruling_optional")}</summary>
          <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
            <div className="lg:col-span-2">
              <Label className="text-slate-300">{t("admin.blog.ruling_headline")}</Label>
              <Input value={rulingHeadline} onChange={(e) => setRulingHeadline(e.target.value)} className="mt-1 border-slate-700 bg-slate-950 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">{t("admin.blog.ruling_court")}</Label>
              <Input value={rulingCourt} onChange={(e) => setRulingCourt(e.target.value)} className="mt-1 border-slate-700 bg-slate-950 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">{t("admin.blog.ruling_number")}</Label>
              <Input value={rulingNumber} onChange={(e) => setRulingNumber(e.target.value)} className="mt-1 border-slate-700 bg-slate-950 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">{t("admin.blog.ruling_date")}</Label>
              <Input value={rulingDate} onChange={(e) => setRulingDate(e.target.value)} placeholder="2024-01-15" className="mt-1 border-slate-700 bg-slate-950 text-white" />
            </div>
            <div className="lg:col-span-2">
              <Label className="text-slate-300">{t("admin.blog.ruling_principle")}</Label>
              <Textarea rows={3} value={rulingPrinciple} onChange={(e) => setRulingPrinciple(e.target.value)} className="mt-1 border-slate-700 bg-slate-950 text-white" />
            </div>
          </div>
        </details>

        <div className="mt-5 flex justify-end">
          <Button
            onClick={handleGenerate}
            disabled={generating || topic.trim().length < 5}
            className="bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
            data-testid="admin-blog-generate-btn"
          >
            {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
            {generating ? t("admin.blog.generating") : t("admin.blog.generate_btn")}
          </Button>
        </div>

        {(draft.title || draft.body_markdown) && (
          <div className="mt-8 border-t border-slate-800 pt-6" data-testid="admin-blog-draft-editor">
            <div className="mb-4 flex items-center gap-2">
              <FileText className="h-4 w-4 text-[#F4C955]" />
              <h4 className="font-display text-base font-bold text-white">
                {draft.post_id ? t("admin.blog.editing_existing") : t("admin.blog.reviewing_draft")}
              </h4>
              <span className="ml-auto rounded-full bg-slate-800 px-2 py-0.5 font-mono text-[10px] text-slate-400">
                {draft.body_markdown.length} chars
              </span>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">{t("admin.blog.field_title")}</Label>
                <Input
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  className="mt-1 border-slate-700 bg-slate-950 text-white"
                  data-testid="admin-blog-field-title"
                />
              </div>
              <div>
                <Label className="text-slate-300">{t("admin.blog.field_meta")}</Label>
                <Textarea
                  rows={2}
                  value={draft.meta_description}
                  onChange={(e) => setDraft({ ...draft, meta_description: e.target.value })}
                  className="mt-1 border-slate-700 bg-slate-950 text-white"
                  data-testid="admin-blog-field-meta"
                />
                <p className="mt-1 text-[10px] text-slate-500">{draft.meta_description.length} / 160 chars</p>
              </div>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div>
                  <Label className="text-slate-300">{t("admin.blog.field_keyword")}</Label>
                  <Input
                    value={draft.keyword}
                    onChange={(e) => setDraft({ ...draft, keyword: e.target.value })}
                    className="mt-1 border-slate-700 bg-slate-950 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">{t("admin.blog.field_tags")}</Label>
                  <Input
                    value={draft.tags}
                    onChange={(e) => setDraft({ ...draft, tags: e.target.value })}
                    placeholder="gdpr, cassazione, contratti"
                    className="mt-1 border-slate-700 bg-slate-950 text-white"
                    data-testid="admin-blog-field-tags"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">{t("admin.blog.field_hero_url")}</Label>
                <Input
                  value={draft.hero_image_url}
                  onChange={(e) => setDraft({ ...draft, hero_image_url: e.target.value })}
                  placeholder="https://..."
                  className="mt-1 border-slate-700 bg-slate-950 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">{t("admin.blog.field_body")}</Label>
                <Textarea
                  rows={18}
                  value={draft.body_markdown}
                  onChange={(e) => setDraft({ ...draft, body_markdown: e.target.value })}
                  className="mt-1 font-mono text-xs border-slate-700 bg-slate-950 text-white"
                  data-testid="admin-blog-field-body"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => handleSave(false)}
                  disabled={saving}
                  variant="outline"
                  className="border-slate-600 text-slate-200 hover:bg-slate-800"
                  data-testid="admin-blog-save-draft"
                >
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  {t("admin.blog.save_draft_btn")}
                </Button>
                <Button
                  onClick={() => handleSave(true)}
                  disabled={saving}
                  className="bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
                  data-testid="admin-blog-save-publish"
                >
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />}
                  {t("admin.blog.save_publish_btn")}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="text-slate-400 hover:text-white"
                  onClick={() => setDraft(EMPTY_DRAFT)}
                >
                  {t("admin.blog.discard_btn")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Posts table */}
      <Card className="border-slate-800 bg-slate-900/60 p-6" data-testid="admin-blog-posts-table">
        <h3 className="mb-4 font-display text-lg font-bold text-white">{t("admin.blog.all_posts")}</h3>
        {loading && (
          <p className="text-center text-sm text-slate-500">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
          </p>
        )}
        {!loading && posts.length === 0 && (
          <p className="text-center text-sm text-slate-500" data-testid="admin-blog-empty">{t("admin.blog.no_posts")}</p>
        )}
        {!loading && posts.length > 0 && (
          <div className="space-y-3">
            {posts.map((post) => (
              <div
                key={post.post_id}
                className="flex flex-col gap-3 rounded-lg border border-slate-800 bg-slate-950/50 p-4 lg:flex-row lg:items-center lg:justify-between"
                data-testid={`admin-blog-row-${post.post_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-mono font-bold uppercase tracking-wider ${
                        post.status === "published"
                          ? "bg-[#9ACD32]/15 text-[#9ACD32]"
                          : "bg-slate-800 text-slate-400"
                      }`}
                    >
                      {post.status}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
                      {post.locale}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
                      {post.reading_time_minutes} min
                    </span>
                    {post.view_count > 0 && (
                      <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
                        {post.view_count} views
                      </span>
                    )}
                  </div>
                  <p className="mt-1 truncate font-display text-sm font-bold text-white">{post.title}</p>
                  <p className="truncate text-xs text-slate-500">{post.slug}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {post.status === "published" && (
                    <a
                      href={`/blog/${post.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded-md border border-slate-700 px-2.5 py-1.5 text-xs font-semibold text-slate-300 hover:border-slate-500 hover:text-white"
                      data-testid={`admin-blog-view-${post.post_id}`}
                    >
                      <ExternalLink className="h-3 w-3" /> View
                    </a>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(post)}
                    className="border-slate-600 text-slate-200 hover:bg-slate-800"
                    data-testid={`admin-blog-edit-${post.post_id}`}
                  >
                    <FileText className="mr-1 h-3 w-3" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleTogglePublish(post)}
                    className={post.status === "published"
                      ? "bg-slate-700 text-white hover:bg-slate-600"
                      : "bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
                    }
                    data-testid={`admin-blog-toggle-${post.post_id}`}
                  >
                    {post.status === "published" ? (
                      <><EyeOff className="mr-1 h-3 w-3" /> Unpublish</>
                    ) : (
                      <><Eye className="mr-1 h-3 w-3" /> Publish</>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(post)}
                    className="border-red-800 text-red-400 hover:bg-red-950/30"
                    data-testid={`admin-blog-delete-${post.post_id}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
